import { ref, reactive } from 'vue'

interface ModalData {
  title?: string
  message?: string
  component?: any
  props?: Record<string, any>
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showCloseButton?: boolean
  showDefaultFooter?: boolean
  showCancelButton?: boolean
  showConfirmButton?: boolean
  cancelText?: string
  confirmText?: string
  confirmButtonClass?: string
  closeOnBackdrop?: boolean
  closeOnEscape?: boolean
}

interface ModalOptions extends ModalData {
  onConfirm?: (result?: any) => void
  onCancel?: () => void
  onClose?: () => void
}

const isVisible = ref(false)
const modalData = reactive<ModalData>({})
let currentResolve: ((value: any) => void) | null = null
let currentReject: ((reason?: any) => void) | null = null
let onConfirmCallback: ((result?: any) => void) | null = null
let onCancelCallback: (() => void) | null = null
let onCloseCallback: (() => void) | null = null

export function useGlobalModal() {
  const openModal = (options: ModalOptions): Promise<any> => {
    return new Promise((resolve, reject) => {
      // Store callbacks
      currentResolve = resolve
      currentReject = reject
      onConfirmCallback = options.onConfirm || null
      onCancelCallback = options.onCancel || null
      onCloseCallback = options.onClose || null

      // Set modal data
      Object.assign(modalData, {
        title: options.title,
        message: options.message,
        component: options.component,
        props: options.props || {},
        size: options.size || 'md',
        showCloseButton: options.showCloseButton ?? true,
        showDefaultFooter: options.showDefaultFooter ?? false,
        showCancelButton: options.showCancelButton ?? true,
        showConfirmButton: options.showConfirmButton ?? true,
        cancelText: options.cancelText || 'Cancelar',
        confirmText: options.confirmText || 'Confirmar',
        confirmButtonClass: options.confirmButtonClass || 'bg-blue-600 hover:bg-blue-700',
        closeOnBackdrop: options.closeOnBackdrop ?? true,
        closeOnEscape: options.closeOnEscape ?? true
      })

      // Show modal
      isVisible.value = true
    })
  }

  const closeModal = (result?: any) => {
    isVisible.value = false
    
    if (result !== undefined && currentResolve) {
      currentResolve(result)
      if (onConfirmCallback) {
        onConfirmCallback(result)
      }
    } else if (result === false && currentReject) {
      currentReject('cancelled')
      if (onCancelCallback) {
        onCancelCallback()
      }
    } else {
      if (currentReject) {
        currentReject('closed')
      }
      if (onCloseCallback) {
        onCloseCallback()
      }
    }

    // Reset callbacks
    currentResolve = null
    currentReject = null
    onConfirmCallback = null
    onCancelCallback = null
    onCloseCallback = null

    // Clear modal data
    Object.assign(modalData, {
      title: undefined,
      message: undefined,
      component: undefined,
      props: {},
      size: 'md',
      showCloseButton: true,
      showDefaultFooter: false,
      showCancelButton: true,
      showConfirmButton: true,
      cancelText: 'Cancelar',
      confirmText: 'Confirmar',
      confirmButtonClass: 'bg-blue-600 hover:bg-blue-700',
      closeOnBackdrop: true,
      closeOnEscape: true
    })
  }

  // Convenience methods
  const showAlert = (title: string, message: string, options?: Partial<ModalOptions>) => {
    return openModal({
      title,
      message,
      showDefaultFooter: true,
      showCancelButton: false,
      confirmText: 'OK',
      ...options
    })
  }

  const showConfirm = (title: string, message: string, options?: Partial<ModalOptions>) => {
    return openModal({
      title,
      message,
      showDefaultFooter: true,
      confirmButtonClass: 'bg-red-600 hover:bg-red-700',
      ...options
    })
  }

  const showCustom = (component: any, props?: Record<string, any>, options?: Partial<ModalOptions>) => {
    return openModal({
      component,
      props,
      showDefaultFooter: false,
      ...options
    })
  }

  return {
    isVisible,
    modalData,
    openModal,
    closeModal,
    showAlert,
    showConfirm,
    showCustom
  }
}

// Global instance for use in plugins or outside of components
export const globalModal = useGlobalModal()